/**
 * Offline Transaction Queue Service
 * Handles storing and syncing transactions when offline
 */

import { useToast } from 'vue-toastification'

const toast = useToast()

class OfflineQueueService {
  constructor() {
    this.queueKey = 'quickbill_offline_transactions'
    this.syncInProgress = false
    this.maxRetries = 3
    this.retryDelay = 5000 // 5 seconds
  }

  /**
   * Add transaction to offline queue
   * @param {Object} transactionData - Transaction data to queue
   * @returns {string} Queue item ID
   */
  queueTransaction(transactionData) {
    const queueItem = {
      id: this.generateId(),
      data: transactionData,
      timestamp: new Date().toISOString(),
      retries: 0,
      status: 'pending',
      type: 'transaction'
    }

    const queue = this.getQueue()
    queue.push(queueItem)
    this.saveQueue(queue)

    console.log('Transaction queued for offline sync:', queueItem.id)
    // Only show toast when going offline, not for every transaction
    if (!navigator.onLine) {
      toast.info('Transaction saved offline - will sync when online')
    }

    return queueItem.id
  }

  /**
   * Add product update to offline queue
   * @param {Object} productData - Product data to queue
   * @returns {string} Queue item ID
   */
  queueProductUpdate(productData) {
    const queueItem = {
      id: this.generateId(),
      data: productData,
      timestamp: new Date().toISOString(),
      retries: 0,
      status: 'pending',
      type: 'product_update'
    }

    const queue = this.getQueue()
    queue.push(queueItem)
    this.saveQueue(queue)

    // console.log('Product update queued for offline sync:', queueItem.id)
    return queueItem.id
  }

  /**
   * Add customer update to offline queue
   * @param {Object} customerData - Customer data to queue
   * @returns {string} Queue item ID
   */
  queueCustomerUpdate(customerData) {
    const queueItem = {
      id: this.generateId(),
      data: customerData,
      timestamp: new Date().toISOString(),
      retries: 0,
      status: 'pending',
      type: 'customer_update'
    }

    const queue = this.getQueue()
    queue.push(queueItem)
    this.saveQueue(queue)

    // console.log('Customer update queued for offline sync:', queueItem.id)
    return queueItem.id
  }

  /**
   * Get all pending items from queue
   * @returns {Array} Queue items
   */
  getQueue() {
    try {
      const queue = localStorage.getItem(this.queueKey)
      return queue ? JSON.parse(queue) : []
    } catch (error) {
      console.error('Error reading offline queue:', error)
      return []
    }
  }

  /**
   * Save queue to localStorage
   * @param {Array} queue - Queue items to save
   */
  saveQueue(queue) {
    try {
      localStorage.setItem(this.queueKey, JSON.stringify(queue))
    } catch (error) {
      console.error('Error saving offline queue:', error)
      toast.error('Failed to save offline data')
    }
  }

  /**
   * Get queue statistics
   * @returns {Object} Queue stats
   */
  getQueueStats() {
    const queue = this.getQueue()
    return {
      total: queue.length,
      pending: queue.filter(item => item.status === 'pending').length,
      failed: queue.filter(item => item.status === 'failed').length,
      synced: queue.filter(item => item.status === 'synced').length,
      transactions: queue.filter(item => item.type === 'transaction').length,
      products: queue.filter(item => item.type === 'product_update').length,
      customers: queue.filter(item => item.type === 'customer_update').length
    }
  }

  /**
   * Sync all pending items when back online
   * @param {Object} apiService - API service instance
   * @returns {Promise<Object>} Sync results
   */
  async syncQueue(apiService) {
    if (this.syncInProgress) {
      // console.log('Sync already in progress')
      return { success: false, message: 'Sync in progress' }
    }

    this.syncInProgress = true
    const queue = this.getQueue()
    const pendingItems = queue.filter(item => item.status === 'pending' || item.status === 'failed')

    if (pendingItems.length === 0) {
      this.syncInProgress = false
      return { success: true, synced: 0, failed: 0 }
    }

    console.log(`Starting sync of ${pendingItems.length} offline items`)
    // Only show toast for larger queues or first sync
    if (pendingItems.length > 1) {
      toast.info(`Syncing ${pendingItems.length} offline items...`)
    }

    let synced = 0
    let failed = 0

    for (const item of pendingItems) {
      try {
        const success = await this.syncItem(item, apiService)
        if (success) {
          item.status = 'synced'
          item.syncedAt = new Date().toISOString()
          synced++
        } else {
          item.retries++
          if (item.retries >= this.maxRetries) {
            item.status = 'failed'
            failed++
          }
        }
      } catch (error) {
        console.error('Error syncing item:', item.id, error)
        item.retries++
        if (item.retries >= this.maxRetries) {
          item.status = 'failed'
          failed++
        }
      }
    }

    // Save updated queue
    this.saveQueue(queue)

    // Clean up old synced items (older than 24 hours)
    this.cleanupQueue()

    this.syncInProgress = false

    const result = { success: true, synced, failed }
    
    // Only show success toast if multiple items synced
    if (synced > 1) {
      toast.success(`Successfully synced ${synced} items`)
    }
    if (failed > 0) {
      toast.warning(`${failed} items failed to sync`)
    }

    console.log('Sync completed:', result)
    return result
  }

  /**
   * Sync individual item
   * @param {Object} item - Queue item to sync
   * @param {Object} apiService - API service instance
   * @returns {Promise<boolean>} Success status
   */
  async syncItem(item, apiService) {
    try {
      switch (item.type) {
        case 'transaction':
          return await this.syncTransaction(item.data, apiService)
        case 'product_update':
          return await this.syncProductUpdate(item.data, apiService)
        case 'customer_update':
          return await this.syncCustomerUpdate(item.data, apiService)
        default:
          console.error('Unknown item type:', item.type)
          return false
      }
    } catch (error) {
      console.error('Error syncing item:', error)
      return false
    }
  }

  /**
   * Sync transaction to server
   * @param {Object} transactionData - Transaction data
   * @param {Object} apiService - API service instance
   * @returns {Promise<boolean>} Success status
   */
  async syncTransaction(transactionData, apiService) {
    try {
      const response = await apiService.post('/transactions', transactionData)
      // console.log('Transaction synced successfully:', response)
      return true
    } catch (error) {
      console.error('Failed to sync transaction:', error)
      return false
    }
  }

  /**
   * Sync product update to server
   * @param {Object} productData - Product data
   * @param {Object} apiService - API service instance
   * @returns {Promise<boolean>} Success status
   */
  async syncProductUpdate(productData, apiService) {
    try {
      const { id, ...updateData } = productData
      const response = await apiService.put(`/products/${id}`, updateData)
      // console.log('Product update synced successfully:', response)
      return true
    } catch (error) {
      console.error('Failed to sync product update:', error)
      return false
    }
  }

  /**
   * Sync customer update to server
   * @param {Object} customerData - Customer data
   * @param {Object} apiService - API service instance
   * @returns {Promise<boolean>} Success status
   */
  async syncCustomerUpdate(customerData, apiService) {
    try {
      const { id, ...updateData } = customerData
      const response = await apiService.put(`/customers/${id}`, updateData)
      // console.log('Customer update synced successfully:', response)
      return true
    } catch (error) {
      console.error('Failed to sync customer update:', error)
      return false
    }
  }

  /**
   * Remove old synced items from queue
   */
  cleanupQueue() {
    const queue = this.getQueue()
    const cutoffTime = new Date()
    cutoffTime.setHours(cutoffTime.getHours() - 24) // 24 hours ago

    const cleanedQueue = queue.filter(item => {
      if (item.status === 'synced' && new Date(item.syncedAt || item.timestamp) < cutoffTime) {
        return false // Remove old synced items
      }
      return true // Keep item
    })

    if (cleanedQueue.length < queue.length) {
      console.log(`Cleaned up ${queue.length - cleanedQueue.length} old synced items`)
      this.saveQueue(cleanedQueue)
    }
  }

  /**
   * Clear all items from queue
   */
  clearQueue() {
    localStorage.removeItem(this.queueKey)
    console.log('Offline queue cleared')
    // toast.info('Offline queue cleared') // Removed - only for manual clear actions
  }

  /**
   * Remove specific item from queue
   * @param {string} itemId - Item ID to remove
   */
  removeItem(itemId) {
    const queue = this.getQueue()
    const filteredQueue = queue.filter(item => item.id !== itemId)
    this.saveQueue(filteredQueue)
    console.log('Item removed from queue:', itemId)
  }

  /**
   * Retry failed items
   * @param {Object} apiService - API service instance
   */
  async retryFailed(apiService) {
    const queue = this.getQueue()
    const failedItems = queue.filter(item => item.status === 'failed')

    if (failedItems.length === 0) {
      // toast.info('No failed items to retry') // Removed - not important info
      return
    }

    // Reset failed items to pending with reset retry count
    failedItems.forEach(item => {
      item.status = 'pending'
      item.retries = 0
    })

    this.saveQueue(queue)
    console.log(`Retrying ${failedItems.length} failed items`)
    // Only show toast for user-initiated retry actions
    // toast.info(`Retrying ${failedItems.length} failed items`)

    // Trigger sync
    return await this.syncQueue(apiService)
  }

  /**
   * Generate unique ID for queue items
   * @returns {string} Unique ID
   */
  generateId() {
    return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Export queue data for debugging
   * @returns {Object} Queue data
   */
  exportQueue() {
    return {
      queue: this.getQueue(),
      stats: this.getQueueStats(),
      timestamp: new Date().toISOString()
    }
  }
}

// Create and export singleton instance
export const offlineQueue = new OfflineQueueService()
export default offlineQueue